import React, {useCallback, useEffect, useRef, useState} from "react";
import {Typography, IconButton} from '@bonprix/design-system';
import Landmark from '@core/scripts/components/landmark-adapter';
import Url from '@core/scripts/helper/url.js';

const NavigationItemsSlider =  ({ items, activeId, setActiveId, setNextActiveId, ref, texts }) => {
     const itemsToShow = items.filter(item => !item.overviewItem)
      const itemRefs = useRef(new Map());
      const activeLinesRefs = useRef(new Map());
     const enableScroll = useRef(true);
     const sliderRef = useRef(null);
     const [showPrev, setShowPrev] = useState(false);
     const [showNext, setShowNext] = useState(false);

     const updateButtonVisibility = useCallback(() => {
         const slider = sliderRef.current;
         if (slider) {
             const shouldShowPrev = slider.scrollLeft > 0;
             const shouldShowNext = slider.scrollLeft + slider.clientWidth < slider.scrollWidth - 1;
             setShowPrev(shouldShowPrev);
             setShowNext(shouldShowNext);
         } else {
             setShowPrev(false);
             setShowNext(false);
         }
     }, []);

     useEffect(() => {
         if (enableScroll.current && activeId) {
             const element = itemRefs.current.get(activeId);
             if (element) {
                 initialScroll(element);
             }
         }
         enableScroll.current = true;
     }, [activeId]);

     useEffect(() => {
         updateButtonVisibility();
     }, [items]);

    const handleNextClick = useCallback(() => {
        const slider = sliderRef.current;
        if (slider) {
            slider.scrollBy({left: slider.clientWidth, behavior: 'smooth'});
        }
    }, []);

    const handlePrevClick = useCallback(() => {
        const slider = sliderRef.current;
        if (slider) {
            const target = Math.max(0, slider.scrollLeft - slider.clientWidth);
            slider.scrollTo({left: target, behavior: 'smooth'});
        }
    }, []);

     const initialScroll = (element) => {
         const parent = element.parentNode;
         const parentRect = parent.getBoundingClientRect();
         const elementRect = element.getBoundingClientRect();

         const wishedX = parentRect.left + parentRect.width / 2 - elementRect.width / 2;
         const scrollX = elementRect.left - wishedX;

         if (parentRect.left !== 0 ||
             (elementRect.right > parentRect.width || elementRect.left < parentRect.left || elementRect.left < 0)) {
             parent.scrollBy(scrollX, 0);
         }
     };

     const handleClick = (event, item) => {
         event.preventDefault();

         const isActiveItem = item.id === activeId;
         const naviLmValue = `hn\\${item.id}${isActiveItem ? '\\allSelect\\' : '\\\\'}`;
         Landmark.sendUserInterfaceLandmark({navi: naviLmValue});

         if (isActiveItem) {
             window.location.href =  Url.toAbsolute(item.url)
         } else {
             setNextActiveId(item.id);
             const activeBar = activeLinesRefs.current.get(activeId);
             const translateX = calculateTranslateX(activeBar, item.id);

             const nextElement = itemRefs.current.get(item.id);
             const nextElementWidth = nextElement.getBoundingClientRect().width;

             activeBar.style.transition = 'width 0.35s ease, transform 0.3s ease';
             activeBar.style.transform = `translateX(${translateX}px)`;
             activeBar.style.width = `${nextElementWidth}px`;

             enableScroll.current = false;
             initialScroll(nextElement);

             setTimeout(() => {
                 setActiveId(item.id);
                 setNextActiveId(null);
             }, 300);
         }
     }

     const calculateTranslateX = (activeBar, nextItemId) => {
         const nextElement = itemRefs.current.get(nextItemId);
         const nextElementX = nextElement.getBoundingClientRect().x;
         const activeBarX = activeBar.getBoundingClientRect().x;
         return nextElementX - activeBarX;
     }

     return (
         <div className="navigation__items-slider-wrapper" ref={ref}>
             {showPrev && (
                 <div className="navigation__slider-btn bg-clr-on-primary-high -prev">
                     <IconButton
                         icon="arrow-left"
                         variant="tertiary"
                         aria-label={texts.slider_prev_aria_label}
                         onClick={handlePrevClick}
                     />
                 </div>
             )}
             <div className="navigation__items-slider" ref={sliderRef} onScroll={updateButtonVisibility}>
                 {itemsToShow.map((item) => {
                     const titleColor = item.fontColor || "primary";
                     return (
                     <a key={item.id} ref={el => itemRefs.current.set(item.id, el)} href={item.url} className="navigation__items-slider-item"
                        onClick={(event) => handleClick(event, item)}>
                         <Typography color={titleColor} variant="bonprix-h6">{item.title}</Typography>
                         {activeId === item.id &&
                             <div ref={el => activeLinesRefs.current.set(item.id, el)} className="navigation__slider-active-line"></div>}
                     </a>
                     )
                    })
                 }
             </div>
             {showNext && (
                 <div className="navigation__slider-btn bg-clr-on-primary-high -next">
                     <IconButton
                         icon="arrow-right"
                         variant="tertiary"
                         aria-label={texts.slider_next_aria_label}
                         onClick={handleNextClick}
                     />
                 </div>
             )}
         </div>
     );

 };

export default NavigationItemsSlider;